import React, {PropTypes} from 'react';
import {isNull, orderBy} from 'lodash';
import shortid from 'shortid';
import IconButton from 'material-ui/IconButton';
import DeleteIcon from '../../../common/svgs/DeleteIcon';
import SimpleFileInput, { RetrievalButton } from '../../../common/components/c_SourceSimpleFileInput';
import TextField from '../../../common/components/c_TextField';
import DropDownMenu from '../../../common/components/c_DropDownMenu';
import MaskedTextField from '../../../common/components/c_MaskedTextField';

function ScoreProficiency(props) {

  const { test, onChange } = props;

  const handleChange = (event, value) => {
    if (onChange) {
      onChange(event, test.id, 'result', value);
    }
  };

  return (
    <DropDownMenu
      className='result'
      menuItems={['Not Proficient', 'Proficient', 'Highly Proficient', 'Beginner', 'Foundational', 'Intermediate', 'Skilled', 'Advanced']}
      value={test.result}
      primaryText={test.result}
      onChange={handleChange}
    />
  );

}

ScoreProficiency.propTypes = {
  test    : PropTypes.object.isRequired,
  onChange: PropTypes.func
};

function Score(props) {

  const  {
    test,
    onChange
  } = props;

  const rawScoreClass = test.rawScore ? ((test.rawScore >= test.passingScore) ? 'sufficient' : 'insufficient') : '';
  const handleChange = (prop) =>
    (event, value) =>
      {
        if (onChange) {
          onChange(event, test.id, prop, Number.parseInt(value, 10) || null);
        }
      }
  ;

  const rawScore = isNull(test.rawScore) ? '' : test.rawScore;
  const precentile = isNull(test.percentile) ? '' : test.percentile;
  const percentage = test.totalQuestions ? (`${(test.rawScore || 0) * 100 / test.totalQuestions} %`) : 'N/A';

  return (
    <div className='score-container'>
      <MaskedTextField
        className={`value ${rawScoreClass}`}
        mask='999'
        hideMaskChar='true'
        value={rawScore}
        onChange={handleChange('rawScore')}
      />
      <div className='percentage-container'>
        <span className='prefix'>(</span>
          <div className='percentage'>{`${percentage}`}</div>
        <span className='suffix'>)</span>
      </div>
      <MaskedTextField
        className='percentile'
        mask='999'
        hideMaskChar='true'
        value={precentile}
        onChange={handleChange('percentile')}
      />
    </div>
  );

}

Score.propTypes = {
  test    : PropTypes.object.isRequired,
  onChange: PropTypes.func
};

function AssessmentScore(props) {

  const {
    test,
    onChange
  } = props;

  if (test.passingScore) {
    return (
      <div className='score-container'>
        <Score test={test} onChange={onChange} />
      </div>
    );
  }

  return (
    <ScoreProficiency test={test} onChange={onChange} />
  );
}

AssessmentScore.propTypes = {
  test    : PropTypes.object.isRequired,
  onChange: PropTypes.func
};

function AssessmentDocumentViewer(props) {

  const {
    test,
    onRemove
  } = props;

  const handleRemove = () => {
    if (onRemove) {
      onRemove(test.id, test.clientId);
    }
  };

  return (
    <div className='attachment-wrapper'>
      <IconButton onClick={handleRemove} tooltip='Delete' tooltipPosition='bottom-left' className='remove-section' data-id='remove-section'>
        <DeleteIcon/>
      </IconButton>
      <RetrievalButton
        accountId={() => test.accountId}
        className='file-get assessment-file'
        loadingClass=''
        notLoadingClass=''
        failureClass=''
        autoLoad
        noMessage
        path={test.storagePath}
        signingRoute='/api/s3/get-sign'
      >
        <TextField
          disabled
          value={test.fileName}
          inputStyle={{
            textOverflow: 'ellipsis'
          }}
        />
      </RetrievalButton>
    </div>
  );
}

AssessmentDocumentViewer.propTypes = {
  test    : PropTypes.object.isRequired,
  onRemove: PropTypes.func
};

function AssessmentDocumentUploader(props) {

  const {
    test,
    onUpload
  } = props;

  const clientId =  shortid.generate();

  const handleUpload = (err, fileInfo) => {
    if (onUpload) {
      onUpload(test.id, clientId, fileInfo);
    }
  };

  return (
    <div className='attachment-wrapper'>
      <SimpleFileInput
        className='file-uploader'
        noMessage
        pristineClass=''
        loadingClass=''
        successClass=''
        failureClass=''
        accept={['.pdf', '.doc', '.docx', '.tiff', '.txt', '.JPG', '.JPEG', '.jpg', '.jpeg', '.png']}
        remoteFolder='applicants'
        signingRoute='/api/s3/put-sign'
        maxSize={process.env.AWS_FILE_UPLOAD_SIZE}
        onLoadStart={handleUpload}
        onS3Load={(err) => {err && console.error(err)}}
      >
        <div className='upload-file'>Upload File</div>
      </SimpleFileInput>
    </div>
  );

}

AssessmentDocumentUploader.propTypes = {
  test      : PropTypes.object.isRequired,
  onUpload  : PropTypes.func
};

function AssessmentDocument(props) {

  const {
    test,
    onUpload,
    onRemove
  } = props;

  if (test.documentId) {
    return (
      <AssessmentDocumentViewer test={test} onRemove={onRemove} />
    );
  }

  return (
    <AssessmentDocumentUploader test={test} onUpload={onUpload} />
  );

}

AssessmentDocument.propTypes = {
  test      : PropTypes.object.isRequired,
  document  : PropTypes.object,
  onUpload  : PropTypes.func,
  onRemove  : PropTypes.func
};

const assessmentItemTextStyle = {
  borderBottom  : '1px solid #333',
  fontSize: '14px',
  margin  : '7px',
  padding : '10px 0px 10px 0px',
  width   : '100%'
};

function AssessmentTest(props) {

  const {
    test,
    onChange,
    onUpload,
    onRemove
  } = props;

  return (
    <div className='assessment-item'>
      <div style={assessmentItemTextStyle}>{`${test.name}`}</div>
      <div className='attachment'>
        <AssessmentDocument test={test} document={test} onUpload={onUpload} onRemove={onRemove} />
      </div>
      <AssessmentScore test={test} onChange={onChange} />
    </div>
  );
}

AssessmentTest.propTypes = {
  test      : PropTypes.object.isRequired,
  onChange  : PropTypes.func,
  onUpload  : PropTypes.func,
  onRemove  : PropTypes.func
};

export default function Assessments(props) {

  const {
    tests
  } = props;

  const testData = orderBy(tests, ['typeId', 'name'], ['asc']);

  return (
    <div className='body-wrapper'>
      <div className='assessments-container'>
        <div className='assessment-item columns'>
          <div className='test-name'>Assessment</div>
          <div className='attachment'>File Name</div>
          <div className='score-container'>Score</div>
        </div>
        {
          testData.map(test =>
            <AssessmentTest
              key={test.id}
              test={test}
              onChange={props.onChange}
              onUpload={props.onUpload}
              onRemove={props.onRemove}
            />
          )
        }
      </div>
    </div>
  );

}

Assessments.propTypes = {
  tests     : PropTypes.array.isRequired,
  onChange  : PropTypes.func,
  onUpload  : PropTypes.func,
  onRemove  : PropTypes.func
};



// WEBPACK FOOTER //
// ./shared/react/admin/Applicant/Assessments.js